// public/js/features/feedback/feedback-page.js
import { listThreads, getThread, createThread, patchThread, deleteThread, addComment, voteThread, attachmentObjectUrl } from './api.js';
import { renderMarkdown, captureContext, relativeTime, STATUS_BADGE, PRIORITY_BADGE, TYPE_BADGE } from './util.js';
import { createAttachmentBar } from './uploader.js';
import { escapeHtml } from '../../utils/escape.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { loadTenants } from '../../modules/tenants.js';
import { showConfirm } from '../../modules/ui/confirm.js';

const STATUSES = ['open', 'acknowledged', 'in_progress', 'planned', 'resolved', 'released', 'closed', 'wontfix', 'duplicate'];
const PRIORITIES = ['low', 'normal', 'high', 'critical'];
const LAST_SEEN_KEY = 'feedback.lastSeenAt';

let canManage = false;

export async function initFeedbackPage(container) {
  canManage = can(getCurrentUser().groups || [], 'manageFeedback');
  const m = (location.hash || '').match(/^#feedback\/(.+)$/);
  if (m) return renderDetail(container, decodeURIComponent(m[1]));
  // Visiting the list marks everything seen.
  try { localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString()); } catch {}
  setNavBadge(0);
  return renderList(container);
}

// ── list + tabs ──────────────────────────────────────────────────────────────
let currentBoard = 'mine';
async function renderList(container) {
  const tabs = [
    { id: 'mine', label: 'My tickets', icon: 'fa-user' },
    { id: 'tenant', label: 'Tenant', icon: 'fa-building' },
    { id: 'roadmap', label: 'Roadmap', icon: 'fa-map-signs' },
  ];
  if (canManage) tabs.push({ id: 'inbox', label: 'Inbox (all)', icon: 'fa-inbox' });
  if (!tabs.find(t => t.id === currentBoard)) currentBoard = 'mine';

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="h4 mb-0"><i class="fa-solid fa-comments me-2"></i>Feedback</h2>
      <button id="fb-new" class="btn btn-success"><i class="fa-solid fa-plus me-2"></i>New ticket</button>
    </div>
    <ul class="nav nav-tabs mb-3">
      ${tabs.map(t => `<li class="nav-item"><button class="nav-link ${t.id === currentBoard ? 'active' : ''} fb-tab" data-board="${t.id}"><i class="fa-solid ${t.icon} me-1"></i>${t.label}</button></li>`).join('')}
    </ul>
    <div id="fb-list"><div class="text-muted py-4 text-center"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div></div>`;

  container.querySelectorAll('.fb-tab').forEach(b => b.addEventListener('click', () => { currentBoard = b.dataset.board; renderList(container); }));
  container.querySelector('#fb-new').addEventListener('click', () => openNewModal(container));

  const listEl = container.querySelector('#fb-list');
  try {
    const threads = await listThreads(currentBoard);
    if (!threads.length) { listEl.innerHTML = `<div class="text-muted py-4 text-center">No tickets here yet.</div>`; return; }
    listEl.innerHTML = `<div class="list-group">${threads.map(rowHtml).join('')}</div>`;
    listEl.querySelectorAll('.fb-row').forEach(r => r.addEventListener('click', () => { location.hash = `#feedback/${encodeURIComponent(r.dataset.id)}`; }));
  } catch (e) {
    listEl.innerHTML = `<div class="alert alert-danger">${escapeHtml(e.message || 'Failed to load')}</div>`;
  }
}

function badge(text, kind) { return `<span class="badge bg-${kind}">${escapeHtml(text)}</span>`; }

function rowHtml(t) {
  return `
    <button type="button" class="list-group-item list-group-item-action fb-row" data-id="${escapeHtml(t.threadId)}">
      <div class="d-flex align-items-center gap-2">
        ${badge(t.type, TYPE_BADGE[t.type] || 'secondary')}
        <span class="fw-semibold text-truncate">${escapeHtml(t.title)}</span>
        <span class="ms-auto d-flex align-items-center gap-2 small text-muted">
          ${t.votes ? `<span title="Votes"><i class="fa-solid fa-arrow-up"></i> ${t.votes}</span>` : ''}
          ${t.commentCount ? `<span title="Comments"><i class="fa-solid fa-comment"></i> ${t.commentCount}</span>` : ''}
          ${badge(t.status, STATUS_BADGE[t.status] || 'secondary')}
          ${badge(t.priority, PRIORITY_BADGE[t.priority] || 'secondary')}
          <span>${relativeTime(t.lastActivityAt)}</span>
        </span>
      </div>
    </button>`;
}

// ── new-ticket modal ───────────────────────────────────────────────────────
async function openNewModal(container) {
  const id = 'fb-new-modal';
  document.getElementById(id)?.remove();
  // Tenant picker only for multi-tenant users filing a report.
  let tenants = [];
  try { tenants = await loadTenants(); } catch {}
  const typeOptions = canManage
    ? `<option value="report">Bug / Report</option><option value="feature">Feature (roadmap)</option><option value="fix">Fix (roadmap)</option>`
    : `<option value="report">Bug / Report</option>`;

  const html = `
    <div class="modal fade" id="${id}" tabindex="-1"><div class="modal-dialog modal-lg"><div class="modal-content">
      <div class="modal-header"><h5 class="modal-title">New ticket</h5><button class="btn-close" data-bs-dismiss="modal"></button></div>
      <div class="modal-body">
        ${canManage ? `<div class="mb-3"><label class="form-label fw-semibold">Type</label><select id="fb-type" class="form-select">${typeOptions}</select></div>` : `<input type="hidden" id="fb-type" value="report">`}
        ${tenants.length > 1 ? `<div class="mb-3 fb-tenant-wrap"><label class="form-label fw-semibold">Tenant</label><select id="fb-tenant" class="form-select">${tenants.map(t => `<option value="${escapeHtml(t.tenantId)}">${escapeHtml(t.name || t.tenantId)}</option>`).join('')}</select></div>` : ''}
        <div class="mb-3"><label class="form-label fw-semibold">Title <span class="text-danger">*</span></label><input id="fb-title" class="form-control" maxlength="200"></div>
        <div class="mb-2"><label class="form-label fw-semibold">Description</label>
          <textarea id="fb-body" class="form-control" rows="5" placeholder="What happened? Markdown supported. Paste screenshots directly."></textarea></div>
        <div id="fb-attach-slot"></div>
        <div class="form-check mt-3 fb-ctx-wrap"><input class="form-check-input" type="checkbox" id="fb-ctx" checked>
          <label class="form-check-label small text-muted" for="fb-ctx">Attach diagnostics (app version, page, browser, recent errors)</label></div>
      </div>
      <div class="modal-footer"><button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button id="fb-create" class="btn btn-success">Create ticket</button></div>
    </div></div></div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });

  const attach = createAttachmentBar();
  el.querySelector('#fb-attach-slot').appendChild(attach.el);
  attach.bindPaste(el.querySelector('#fb-body'));
  // Diagnostics toggle only meaningful for reports.
  const syncCtx = () => { const isReport = (el.querySelector('#fb-type').value === 'report'); el.querySelector('.fb-ctx-wrap').classList.toggle('d-none', !isReport); };
  el.querySelector('#fb-type').addEventListener?.('change', syncCtx); syncCtx();

  el.querySelector('#fb-create').addEventListener('click', async () => {
    const btn = el.querySelector('#fb-create');
    const title = el.querySelector('#fb-title').value.trim();
    if (!title) { window.showToast('Title is required', 'danger'); return; }
    btn.disabled = true;
    const type = el.querySelector('#fb-type').value;
    const payload = {
      type, title,
      body: el.querySelector('#fb-body').value,
      attachments: attach.getAttachments(),
    };
    if (type === 'report') {
      if (el.querySelector('#fb-ctx')?.checked) payload.context = captureContext();
      const tw = el.querySelector('#fb-tenant'); if (tw) payload.tenantId = tw.value;
    }
    try {
      const { thread } = await createThread(payload);
      modal.hide();
      window.showToast('Ticket created', 'success');
      location.hash = `#feedback/${encodeURIComponent(thread.threadId)}`;
    } catch (e) { window.showToast(e.message || 'Failed', 'danger'); btn.disabled = false; }
  });
  modal.show();
}

// ── thread detail ──────────────────────────────────────────────────────────
async function renderDetail(container, threadId) {
  container.innerHTML = `<div class="text-muted py-4 text-center"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>`;
  let data;
  try { data = await getThread(threadId); }
  catch (e) { container.innerHTML = `<div class="alert alert-danger">${escapeHtml(e.message || 'Not found')}</div><a href="#feedback" class="btn btn-link">← Back</a>`; return; }
  const { thread, comments, linked } = data;
  const me = getCurrentUser();
  const isAuthor = [me.sub, me.userId, me.username].includes(thread.authorUserId)
    || (!!me.email && me.email === thread.authorEmail);

  container.innerHTML = `
    <a href="#feedback" class="btn btn-link px-0 mb-2"><i class="fa-solid fa-arrow-left me-1"></i>Back</a>
    <div class="row g-3">
      <div class="col-lg-8">
        <div class="card shadow-sm mb-3"><div class="card-body">
          <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
            ${badge(thread.type, TYPE_BADGE[thread.type] || 'secondary')}
            ${badge(thread.status, STATUS_BADGE[thread.status] || 'secondary')}
            ${badge(thread.priority, PRIORITY_BADGE[thread.priority] || 'secondary')}
            <button id="fb-vote" class="btn btn-sm ${thread.hasVoted ? 'btn-primary' : 'btn-outline-primary'} ms-auto">
              <i class="fa-solid fa-arrow-up me-1"></i><span id="fb-vote-n">${thread.votes || 0}</span></button>
          </div>
          <h4>${escapeHtml(thread.title)}</h4>
          <div class="text-muted small mb-3">${escapeHtml(thread.authorDisplay || thread.authorEmail || 'user')} · ${relativeTime(thread.createdAt)}</div>
          <div class="fb-md">${renderMarkdown(thread.body)}</div>
          <div class="fb-atts d-flex flex-wrap mt-2">${attachmentsHtml(thread.attachments)}</div>
          ${thread.context ? `<details class="mt-3"><summary class="small text-muted">Diagnostics</summary><pre class="small bg-light p-2 rounded mt-1" style="white-space:pre-wrap;">${escapeHtml(JSON.stringify(thread.context, null, 2))}</pre></details>` : ''}
          ${linked?.length ? `<div class="mt-3"><div class="small fw-semibold text-muted mb-1">Linked</div>${linked.map(l => `<a href="#feedback/${encodeURIComponent(l.threadId)}" class="badge bg-info text-decoration-none me-1">${escapeHtml(l.type)}: ${escapeHtml(l.title)} (${escapeHtml(l.status)})</a>`).join('')}</div>` : ''}
        </div></div>

        <h6 class="text-muted">Comments</h6>
        <div id="fb-comments"></div>

        <div class="card shadow-sm mt-3"><div class="card-body">
          <textarea id="fb-reply" class="form-control" rows="3" placeholder="Write a reply… (Markdown, paste screenshots)"></textarea>
          <div id="fb-reply-attach"></div>
          <div class="d-flex align-items-center mt-2">
            ${canManage ? `<div class="form-check me-auto"><input class="form-check-input" type="checkbox" id="fb-internal"><label class="form-check-label small text-muted" for="fb-internal">Internal note (platform only)</label></div>` : '<span class="me-auto"></span>'}
            <button id="fb-send" class="btn btn-primary">Reply</button>
          </div>
        </div></div>
      </div>

      <div class="col-lg-4">
        ${canManage ? platformPanel(thread) : sidePanel(thread)}
        ${(canManage || isAuthor) ? `<button id="fb-delete" class="btn btn-outline-danger btn-sm w-100 mt-2"><i class="fa-solid fa-trash me-1"></i>Delete ticket</button>` : ''}
      </div>
    </div>`;

  renderComments(container.querySelector('#fb-comments'), comments, threadId);
  loadAttachments(container.querySelector('.fb-atts'), threadId);

  // Vote toggle
  let voted = thread.hasVoted;
  container.querySelector('#fb-vote').addEventListener('click', async () => {
    voted = !voted;
    try { const r = await voteThread(threadId, voted); thread.votes = r.thread.votes; }
    catch { voted = !voted; }
    container.querySelector('#fb-vote-n').textContent = thread.votes || 0;
    container.querySelector('#fb-vote').classList.toggle('btn-primary', voted);
    container.querySelector('#fb-vote').classList.toggle('btn-outline-primary', !voted);
  });

  // Reply composer
  const rAttach = createAttachmentBar();
  container.querySelector('#fb-reply-attach').appendChild(rAttach.el);
  rAttach.bindPaste(container.querySelector('#fb-reply'));
  container.querySelector('#fb-send').addEventListener('click', async () => {
    const btn = container.querySelector('#fb-send');
    const text = container.querySelector('#fb-reply').value.trim();
    const atts = rAttach.getAttachments();
    if (!text && !atts.length) return;
    btn.disabled = true;
    try {
      await addComment(threadId, { body: text, attachments: atts, internal: container.querySelector('#fb-internal')?.checked });
      renderDetail(container, threadId);
    } catch (e) { window.showToast(e.message || 'Failed', 'danger'); btn.disabled = false; }
  });

  // Platform controls
  if (canManage) wirePlatformPanel(container, threadId, thread);

  // Delete
  container.querySelector('#fb-delete')?.addEventListener('click', async () => {
    const ok = await showConfirm({ title: 'Delete ticket?', body: 'This permanently removes the ticket and its comments.', confirmText: 'Delete', variant: 'danger', icon: 'fa-trash' });
    if (!ok) return;
    try { await deleteThread(threadId); window.showToast('Ticket deleted', 'success'); location.hash = '#feedback'; }
    catch (e) { window.showToast(e.message || 'Failed', 'danger'); }
  });
}

function sidePanel(t) {
  return `<div class="card shadow-sm"><div class="card-body small">
    <div class="mb-2"><span class="text-muted">Status</span><br>${badge(t.status, STATUS_BADGE[t.status] || 'secondary')}</div>
    <div class="mb-2"><span class="text-muted">Priority</span><br>${badge(t.priority, PRIORITY_BADGE[t.priority] || 'secondary')}</div>
    ${t.assignee ? `<div class="mb-2"><span class="text-muted">Assignee</span><br>${escapeHtml(t.assignee)}</div>` : ''}
  </div></div>`;
}

function platformPanel(t) {
  return `<div class="card shadow-sm border-secondary"><div class="card-header bg-secondary text-white py-2"><i class="fa-solid fa-sliders me-1"></i>Triage</div>
    <div class="card-body">
      <label class="form-label small mb-1">Status</label>
      <select id="fb-set-status" class="form-select form-select-sm mb-2">${STATUSES.map(s => `<option value="${s}" ${s === t.status ? 'selected' : ''}>${s}</option>`).join('')}</select>
      <label class="form-label small mb-1">Priority</label>
      <select id="fb-set-priority" class="form-select form-select-sm mb-2">${PRIORITIES.map(p => `<option value="${p}" ${p === t.priority ? 'selected' : ''}>${p}</option>`).join('')}</select>
      <label class="form-label small mb-1">Assignee</label>
      <input id="fb-set-assignee" class="form-control form-control-sm mb-2" value="${escapeHtml(t.assignee || '')}" placeholder="email / name">
      <label class="form-label small mb-1">Linked thread IDs <span class="text-muted">(comma-sep)</span></label>
      <input id="fb-set-links" class="form-control form-control-sm mb-2" value="${escapeHtml((t.linkedThreadIds || []).join(', '))}" placeholder="roadmap thread ids">
      <button id="fb-save-triage" class="btn btn-primary btn-sm w-100">Save</button>
    </div></div>`;
}

function wirePlatformPanel(container, threadId, thread) {
  container.querySelector('#fb-save-triage')?.addEventListener('click', async () => {
    const patch = {
      status: container.querySelector('#fb-set-status').value,
      priority: container.querySelector('#fb-set-priority').value,
      assignee: container.querySelector('#fb-set-assignee').value.trim(),
      linkedThreadIds: container.querySelector('#fb-set-links').value.split(',').map(s => s.trim()).filter(Boolean),
    };
    try { await patchThread(threadId, patch); window.showToast('Saved', 'success'); renderDetail(container, threadId); }
    catch (e) { window.showToast(e.message || 'Failed', 'danger'); }
  });
}

function attachmentsHtml(list) {
  return (list || []).map(a => `<img class="fb-att me-2 mb-2" data-key="${escapeHtml(a.key)}" alt="${escapeHtml(a.name)}" title="${escapeHtml(a.name)}" style="max-width:160px;max-height:160px;object-fit:cover;border-radius:8px;border:1px solid #dee2e6;cursor:pointer;">`).join('');
}

function loadAttachments(scopeEl, threadId) {
  if (!scopeEl) return;
  scopeEl.querySelectorAll('img.fb-att').forEach(async (img) => {
    try { img.src = await attachmentObjectUrl(threadId, img.dataset.key); img.addEventListener('click', () => window.open(img.src, '_blank')); }
    catch { img.replaceWith(Object.assign(document.createElement('span'), { className: 'text-muted small me-2', textContent: '🖼 (unavailable)' })); }
  });
}

function renderComments(el, comments, threadId) {
  if (!comments.length) { el.innerHTML = `<div class="text-muted small">No comments yet.</div>`; return; }
  el.innerHTML = comments.map(c => `
    <div class="card shadow-sm mb-2 ${c.internal ? 'border-warning' : ''}"><div class="card-body py-2">
      <div class="small text-muted mb-1">${escapeHtml(c.authorDisplay || c.authorEmail || 'user')} · ${relativeTime(c.createdAt)} ${c.internal ? '<span class="badge bg-warning text-dark ms-1">internal</span>' : ''}</div>
      <div class="fb-md">${renderMarkdown(c.body)}</div>
      <div class="fb-atts d-flex flex-wrap mt-1">${attachmentsHtml(c.attachments)}</div>
    </div></div>`).join('');
  el.querySelectorAll('.fb-atts').forEach(s => loadAttachments(s, threadId));
}

// ── unread badge (sidebar) ───────────────────────────────────────────────────
function setNavBadge(n) {
  const b = document.getElementById('feedback-nav-badge');
  if (b) { b.textContent = n; b.classList.toggle('d-none', !n); }
}

export async function refreshFeedbackBadge() {
  try {
    let lastSeen = '';
    try { lastSeen = localStorage.getItem(LAST_SEEN_KEY) || ''; } catch {}
    const [mine, tenant] = await Promise.all([listThreads('mine').catch(() => []), listThreads('tenant').catch(() => [])]);
    const seen = new Set();
    const all = [...mine, ...tenant].filter(t => { if (seen.has(t.threadId)) return false; seen.add(t.threadId); return true; });
    const n = all.filter(t => lastSeen && t.lastActivityAt > lastSeen).length;
    setNavBadge(n);
  } catch {}
}
